import { useMemo } from 'react';

import { GlobalAccountRoleType, PersonOrgProfile, PortfolioBeneficiaries } from '@ndvr/customer-api';

import { useGlobalAccount } from 'views/household/household-queries';

import { AccountRow, toAccountRow } from './account-model';
import { kindOf, PersonKind, personName } from './onboarding-model';
import { useHouseholdLinks, useHouseholdProfiles, useOnboardingAccounts, useRelations } from './onboarding-tab-queries';

export interface HouseholdPerson {
    profile: PersonOrgProfile;
    kind: PersonKind;
    name: string;
}

/** Everything the Clients and Accounts views read: people with their kind, entity links, accounts, relations. */
export const useOnboardingHousehold = (householdGUID: string) => {
    const { data: globalAccount } = useGlobalAccount(householdGUID);
    const profilesQuery = useHouseholdProfiles(householdGUID);
    const profiles = useMemo(() => profilesQuery.data ?? [], [profilesQuery.data]);
    const links = useHouseholdLinks(profiles);
    const accountsQuery = useOnboardingAccounts(householdGUID);
    const primary = profiles.find((profile) =>
        profile.globalAccountPersonOrgRoles.some(
            (role) =>
                role.globalAccountGUID === householdGUID &&
                role.roleType === GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER,
        ),
    );
    const relationsQuery = useRelations(primary?.personOrgGUID);

    const accounts: AccountRow[] = useMemo(() => (accountsQuery.data ?? []).map(toAccountRow), [accountsQuery.data]);

    const people: HouseholdPerson[] = useMemo(() => {
        const owners = new Set(accounts.flatMap((row) => row.owners));
        return profiles.flatMap((profile) => {
            const kind = kindOf(profile, householdGUID, owners);
            return kind ? [{ profile, kind, name: personName(profile.personOrg) }] : [];
        });
    }, [profiles, accounts, householdGUID]);

    const byGUID = useMemo(() => new Map(profiles.map((profile) => [profile.personOrgGUID, profile])), [profiles]);

    /** The accounts a person owns or is a beneficiary on. */
    const involvement = (personOrgGUID: string) => ({
        owner: accounts.filter((row) => row.owners.includes(personOrgGUID)),
        beneficiary: accounts.filter((row) =>
            beneficiariesOf(row.account.beneficiaries).some((b) => b.personOrgGUID === personOrgGUID),
        ),
    });

    return {
        isLoading: profilesQuery.isLoading || accountsQuery.isLoading || links.isLoading || relationsQuery.isLoading,
        // Only a first load of relations that failed: a failed refetch keeps the cards, and their unsaved edits, up
        isError: profilesQuery.isError || accountsQuery.isError || relationsQuery.isLoadingError,
        profiles,
        byGUID,
        people,
        links: links.data,
        accounts,
        primary,
        relations: relationsQuery.data ?? [],
        nameOf: (personOrgGUID?: string) =>
            personName(personOrgGUID ? byGUID.get(personOrgGUID)?.personOrg : undefined),
        involvement,
        /** The people of these kinds, as select options. */
        peopleOf: (...kinds: PersonKind[]) =>
            people
                .filter((person) => kinds.includes(person.kind))
                .map((person) => ({ value: person.profile.personOrgGUID, label: person.name })),
        tenantGUID: globalAccount?.tenantGUID,
    };
};

export type OnboardingHousehold = ReturnType<typeof useOnboardingHousehold>;

const beneficiariesOf = (beneficiaries?: PortfolioBeneficiaries) => [
    ...(beneficiaries?.primaryBeneficiaries ?? []),
    ...(beneficiaries?.contingentBeneficiaries ?? []),
];
